import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { chargerUtilisateurs, type UtilisateurApp } from '../Auth/moi.ts';

type Filtre = 'TOUS' | 'COACH' | 'SUIVI' | 'NON_COACHE' | 'SANS_PROFIL';

function categorie(u: UtilisateurApp): Exclude<Filtre, 'TOUS'> {
  if (!u.role) return 'SANS_PROFIL';
  if (u.role === 'COACH') return 'COACH';
  return u.coach ? 'SUIVI' : 'NON_COACHE';
}

const ChipRole = ({ u }: { u: UtilisateurApp }) => {
  switch (categorie(u)) {
    case 'COACH':
      return <Chip size="small" color="primary" label="Coach" />;
    case 'SUIVI':
      return <Chip size="small" color="success" variant="outlined" label="Athlète suivi" />;
    case 'SANS_PROFIL':
      return <Chip size="small" color="warning" variant="outlined" label="Profil pas encore choisi" />;
    default:
      return <Chip size="small" variant="outlined" label="Athlète non coaché" />;
  }
};

function formaterDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Utilisateurs deja connectes a l'application et leur role (reserve au
 * coach, cf ReserveCoach dans App.tsx et RoleService.utilisateurs).
 */
export default function Utilisateurs() {
  const [utilisateurs, setUtilisateurs] = React.useState<UtilisateurApp[] | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [filtre, setFiltre] = React.useState<Filtre>('TOUS');

  React.useEffect(() => {
    chargerUtilisateurs()
      .then(setUtilisateurs)
      .catch((e) => setErreur('Chargement impossible : ' + (e as Error).message));
  }, []);

  const compte = (f: Exclude<Filtre, 'TOUS'>) => (utilisateurs ?? []).filter((u) => categorie(u) === f).length;
  const affiches = (utilisateurs ?? []).filter((u) => filtre === 'TOUS' || categorie(u) === filtre);

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 10, md: 10 } }}>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        Comptes qui se sont déjà connectés à l'application, avec le profil choisi à leur première connexion.
      </Typography>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {!utilisateurs && !erreur && <CircularProgress />}

      {utilisateurs && (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filtre}
            onChange={(_, v) => v && setFiltre(v)}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <ToggleButton value="TOUS">Tous ({utilisateurs.length})</ToggleButton>
            <ToggleButton value="COACH">Coachs ({compte('COACH')})</ToggleButton>
            <ToggleButton value="SUIVI">Athlètes suivis ({compte('SUIVI')})</ToggleButton>
            <ToggleButton value="NON_COACHE">Non coachés ({compte('NON_COACHE')})</ToggleButton>
            {compte('SANS_PROFIL') > 0 && (
              <ToggleButton value="SANS_PROFIL">Sans profil ({compte('SANS_PROFIL')})</ToggleButton>
            )}
          </ToggleButtonGroup>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Club</TableCell>
                  <TableCell>Coach / athlètes</TableCell>
                  <TableCell>Dernière connexion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiches.map((u) => (
                  <TableRow key={u.ssoUserId}>
                    <TableCell>
                      <div>{u.nom || u.email || 'Compte sans email'}</div>
                      {u.nom && u.email && (
                        <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                      )}
                      {!u.email && (
                        <Typography variant="body2" color="text.secondary">
                          Pas reconnecté depuis la mise en place des rôles
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><ChipRole u={u} /></TableCell>
                    <TableCell>{u.club ?? '—'}</TableCell>
                    <TableCell>
                      {u.role === 'COACH'
                        ? `${u.nbAthletes} athlète${u.nbAthletes > 1 ? 's' : ''}`
                        : u.coach ?? '—'}
                    </TableCell>
                    <TableCell>{formaterDate(u.derniereConnexion)}</TableCell>
                  </TableRow>
                ))}
                {affiches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">Aucun utilisateur.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
